import React from 'react';
import { Card, Row, Col, Table, Tag, Empty, Tooltip, Space, Typography } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrophyOutlined,
  RiseOutlined,
  SendOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { PushPerformanceResponse } from 'types/push.types';
import { AppStatsCard } from './AppStatsCard';
import { ChartTooltip } from './ChartTooltip';
import { useChartTheme } from './chartTheme';
import styles from '../../pages/Statistics/statistics.module.css';

const { Text } = Typography;

interface PushPerformanceChartProps {
  data: PushPerformanceResponse;
}

const typeTranslations: Record<string, string> = {
  welcome: 'Приветственные',
  promo: 'Промо',
  reminder: 'Напоминания',
  custom: 'Кастомные',
  system: 'Системные',
};

const ctrTagColor = (ctr: number) => {
  if (ctr > 5) return 'success';
  if (ctr > 2) return 'processing';
  return 'default';
};

export const PushPerformanceChart: React.FC<PushPerformanceChartProps> = ({ data }) => {
  const chart = useChartTheme();

  const typeData = Object.entries(data.by_type).map(([type, stats]) => ({
    type: typeTranslations[type] || type,
    sent: stats.sent,
    clicked: stats.clicked,
    ctr: stats.ctr,
    conversions: stats.registrations + stats.deposits,
  }));

  const ctrData = [...typeData].sort((a, b) => b.ctr - a.ctr);

  const columns = [
    {
      title: 'Заголовок',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string) => (
        <Tooltip title={title}>
          <Text>{title}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Отправлено',
      dataIndex: 'sent',
      key: 'sent',
      width: 120,
      sorter: (a: any, b: any) => a.sent - b.sent,
      render: (sent: number) => <Text strong>{sent.toLocaleString('ru-RU')}</Text>,
    },
    {
      title: 'Клики',
      dataIndex: 'clicked',
      key: 'clicked',
      width: 100,
      sorter: (a: any, b: any) => a.clicked - b.clicked,
      render: (clicked: number) => <Text strong>{clicked.toLocaleString('ru-RU')}</Text>,
    },
    {
      title: 'CTR',
      dataIndex: 'ctr',
      key: 'ctr',
      width: 110,
      sorter: (a: any, b: any) => a.ctr - b.ctr,
      render: (ctr: number) => <Tag color={ctrTagColor(ctr)}>{ctr.toFixed(2)}%</Tag>,
    },
    {
      title: 'Конверсии',
      dataIndex: 'conversions',
      key: 'conversions',
      width: 120,
      sorter: (a: any, b: any) => a.conversions - b.conversions,
      render: (conversions: number) =>
        conversions > 0 ? (
          <Tag color="success">
            <TrophyOutlined /> {conversions}
          </Tag>
        ) : (
          <Text type="secondary">0</Text>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} lg={6}>
          <AppStatsCard title="Отправлено" value={data.summary.total_sent} icon={<SendOutlined />} compact />
        </Col>
        <Col xs={12} lg={6}>
          <AppStatsCard
            title="Доставлено"
            value={data.summary.total_delivered}
            icon={<CheckCircleOutlined />}
            className={styles.success}
            compact
          />
        </Col>
        <Col xs={12} lg={6}>
          <AppStatsCard
            title="Кликов"
            value={data.summary.total_clicked}
            icon={<RiseOutlined />}
            className={styles.info}
            compact
          />
        </Col>
        <Col xs={12} lg={6}>
          <AppStatsCard
            title="Средний CTR"
            value={`${data.summary.avg_ctr.toFixed(2)}%`}
            icon={<BarChartOutlined />}
            className={styles.warning}
            compact
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span className={styles.chartTitle}>
                <div className={styles.chartTitleIcon}>
                  <BarChartOutlined />
                </div>
                Отправки и клики по типам
              </span>
            }
            className={styles.statsCard}
          >
            {typeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={typeData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                  <XAxis
                    dataKey="type"
                    tick={chart.tick}
                    tickLine={false}
                    axisLine={{ stroke: chart.grid }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={64}
                  />
                  <YAxis tick={chart.tick} tickLine={false} axisLine={false} />
                  <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: chart.cursor }} />
                  <Legend wrapperStyle={{ paddingTop: 8, fontSize: 12 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="sent" name="Отправлено" fill={chart.series[0]} radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="clicked" name="Клики" fill={chart.series[1]} radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <span className={styles.chartTitle}>
                <div className={styles.chartTitleIcon}>
                  <RiseOutlined />
                </div>
                CTR по типам
              </span>
            }
            className={styles.statsCard}
          >
            {ctrData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={ctrData} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} horizontal={false} />
                  <XAxis type="number" tick={chart.tick} tickLine={false} axisLine={false} unit="%" />
                  <YAxis
                    type="category"
                    dataKey="type"
                    tick={chart.tick}
                    tickLine={false}
                    axisLine={false}
                    width={120}
                  />
                  <RechartsTooltip
                    content={<ChartTooltip formatter={(value) => `${value.toFixed(2)}%`} />}
                    cursor={{ fill: chart.cursor }}
                  />
                  <Bar dataKey="ctr" name="CTR" fill={chart.series[2]} radius={[0, 4, 4, 0]} maxBarSize={24} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <span className={styles.chartTitle}>
            <div className={styles.chartTitleIcon}>
              <TrophyOutlined />
            </div>
            Топ push-уведомлений
          </span>
        }
        className={styles.statsCard}
      >
        <Table
          columns={columns}
          dataSource={data.top_performing}
          rowKey="message_id"
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Всего: ${total}`, size: 'small' }}
          size="small"
          locale={{ emptyText: 'Нет данных' }}
        />
      </Card>
    </Space>
  );
};